"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import { BellAlertIcon } from "@heroicons/react/24/outline";
import { useAuth } from "@/contexts/AuthContext";
import { useMarketPushReminderEligible } from "@/lib/hooks/useMarketPushReminderEligible";
import { recordNearMeFunnelMetric } from "@/lib/nearMeMetricsClient";
import { MARKET_NEAR_ME_ENTRYPOINT_TARGETS } from "@/lib/push-notifications/near-me-notification-metrics";

type MarketNearMePushReminderProps = {
  nearMePlatformEnabled: boolean;
};

function MarketPushAlertPreview() {
  return (
    <div
      className="shrink-0 w-full max-w-[11rem] rounded-lg border border-border/90 bg-surface px-2.5 py-2 shadow-sm sm:max-w-[12.5rem]"
      aria-hidden
    >
      <div className="flex items-center gap-1.5 text-[10px] text-text-light">
        <span className="flex h-4 w-4 items-center justify-center rounded bg-primary/15 text-primary">
          <BellAlertIcon className="h-2.5 w-2.5" />
        </span>
        <span>Sell It Off</span>
        <span className="text-text-light">·</span>
        <span>Just now</span>
      </div>
      <p className="mt-1.5 text-xs font-medium leading-snug text-foreground">
        New bed near you
      </p>
      <p className="mt-0.5 text-[10px] text-text-light">
        Victoria Island · ₦85,000
      </p>
    </div>
  );
}

export default function MarketNearMePushReminder({
  nearMePlatformEnabled,
}: MarketNearMePushReminderProps) {
  const { user } = useAuth();
  const { eligible, prefsLoaded, webPush } = useMarketPushReminderEligible(
    nearMePlatformEnabled,
  );
  const impressionLogged = useRef(false);

  useEffect(() => {
    if (!eligible || impressionLogged.current) {
      return;
    }
    impressionLogged.current = true;
    void recordNearMeFunnelMetric("near_me_entrypoint_impression", {
      source: "market",
      target: MARKET_NEAR_ME_ENTRYPOINT_TARGETS.pushReminderBanner,
    });
  }, [eligible]);

  if (!user || !prefsLoaded || !eligible) {
    return null;
  }

  return (
    <div className="mb-4 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3 text-sm text-foreground">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          <p className="font-medium text-foreground">
            Know when new items post near you
          </p>
          <p className="text-xs text-text-light">
            You&apos;re already watching your area. Get a ping on this phone
            when something new goes live — no need to keep checking back.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              onClick={() => {
                void recordNearMeFunnelMetric("near_me_entrypoint_click", {
                  source: "market",
                  target: MARKET_NEAR_ME_ENTRYPOINT_TARGETS.enablePushInline,
                });
                void webPush.enablePush({ source: "market" });
              }}
              disabled={webPush.busy}
            >
              {webPush.busy ? "Turning on…" : "Turn on phone alerts"}
            </button>
            <Link
              href="/profile?tab=notifications&nearMeSource=market"
              className="text-xs text-text-light hover:text-foreground hover:underline"
              onClick={() => {
                void recordNearMeFunnelMetric("near_me_entrypoint_click", {
                  source: "market",
                  target: MARKET_NEAR_ME_ENTRYPOINT_TARGETS.profileDeeplink,
                });
              }}
            >
              Alert settings
            </Link>
          </div>
          {webPush.lastError ? (
            <p className="text-xs text-red-600" role="alert">
              {webPush.lastError}
            </p>
          ) : null}
        </div>
        <MarketPushAlertPreview />
      </div>
    </div>
  );
}
